import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    elementIssuesAPI,
    ElementClarifyingQuestion,
    ElementIssuesContext,
    isSlicerGovernanceQuestionId,
    QuestionStatus,
    RawClarifyingQuestion,
    RawIssuesContext,
    SlicerAnswerOutcome,
} from 'api/element-issues';
import { datasetIssuesAPI } from 'api/dataset-issues';
import { domainIssuesAPI } from 'api/domain-issues';

export type EntityIssuesType = 'element' | 'dataset' | 'domain';

const isRenderable = (q: RawClarifyingQuestion): q is ElementClarifyingQuestion =>
    'Superseded' !== q.status && 'Expired' !== q.status;

const selectRenderable = (raw: RawIssuesContext): ElementIssuesContext => ({
    ...raw,
    clarifyingQuestions: raw.clarifyingQuestions.filter(isRenderable),
});

export interface UseEntityIssuesResult {
    context: ElementIssuesContext | null;
    loading: boolean;
    unansweredCount: number;
    unresolvedCount: number;
    answerQuestion: (questionId: string, answer: string) => Promise<SlicerAnswerOutcome | void>;
    skipQuestion: (questionId: string) => Promise<void>;
    skipAllQuestions: () => Promise<void>;
    undoQuestion: (questionId: string) => Promise<void>;
}

const QUERY_KEY = (id: number, type: EntityIssuesType) => ['entityIssues', type, id];

const getAPI = (type: EntityIssuesType) =>
    type === 'dataset' ? datasetIssuesAPI : type === 'domain' ? domainIssuesAPI : elementIssuesAPI;

export function useEntityIssues(id: number, type: EntityIssuesType, enabled: boolean): UseEntityIssuesResult {
    const queryClient = useQueryClient();
    const api = getAPI(type);

    const { data: context = null, isLoading } = useQuery<RawIssuesContext, Error, ElementIssuesContext>({
        queryKey: QUERY_KEY(id, type),
        queryFn: () => api.getContext(id),
        select: selectRenderable,
        enabled,
        staleTime: 60 * 1000,
        // Counts reflect server-side state: re-fetch on panel open (§7.4) and when the user
        // returns from resolving externally, e.g. the Measure Conflicts tab (§8.7).
        // Global defaults disable both.
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });

    const patchQuestion = useCallback(
        (questionId: string, patch: Partial<ElementClarifyingQuestion>) => {
            queryClient.setQueryData<RawIssuesContext>(QUERY_KEY(id, type), (old) => {
                if (!old) return old;
                return {
                    ...old,
                    clarifyingQuestions: old.clarifyingQuestions.map((q) =>
                        q.questionId === questionId ? { ...q, ...patch } : q,
                    ),
                };
            });
        },
        [queryClient, id, type],
    );

    const invalidateOtherEntities = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: ['entityIssues'],
            predicate: (query) => {
                const [, queryType, queryId] = query.queryKey as [string, EntityIssuesType, number];
                return queryType !== type || queryId !== id;
            },
        });
    }, [queryClient, id, type]);

    const unansweredCount = useMemo(() => {
        if (!context) return 0;
        return context.clarifyingQuestions.filter((q) => q.status === 'Unanswered').length;
    }, [context]);

    const unresolvedCount = useMemo(() => {
        if (!context) return 0;
        return context.inconsistencies.length;
    }, [context]);

    const answerQuestion = useCallback(
        async (questionId: string, answer: string): Promise<SlicerAnswerOutcome | void> => {
            if (isSlicerGovernanceQuestionId(questionId)) {
                // Slicer governance answers apply per item: marking the whole
                // grouped question Answered after one item would be wrong, so
                // there is no optimistic patch. The response's per-item
                // results are the control's primary outcome signal; the
                // awaited refetch only reconciles the projection, so its
                // failure is reported (`reconciled: false`) rather than
                // thrown — the answer itself already landed.
                const results = await api.answerQuestion(id, questionId, answer);
                let reconciled = true;
                try {
                    await queryClient.invalidateQueries({ queryKey: QUERY_KEY(id, type) }, { throwOnError: true });
                } catch {
                    reconciled = false;
                }
                invalidateOtherEntities();
                return { results: results ?? undefined, reconciled };
            }
            const snapshot = queryClient.getQueryData<RawIssuesContext>(QUERY_KEY(id, type));
            patchQuestion(questionId, { status: 'Answered', answer });
            try {
                await api.answerQuestion(id, questionId, answer);
            } catch (error) {
                queryClient.setQueryData(QUERY_KEY(id, type), snapshot);
                throw error;
            }
            invalidateOtherEntities();
        },
        [id, type, api, queryClient, patchQuestion, invalidateOtherEntities],
    );

    const skipQuestion = useCallback(
        async (questionId: string) => {
            const snapshot = queryClient.getQueryData<RawIssuesContext>(QUERY_KEY(id, type));
            patchQuestion(questionId, { status: 'Skipped' });
            try {
                await api.skipQuestion(id, questionId);
            } catch (error) {
                queryClient.setQueryData(QUERY_KEY(id, type), snapshot);
                throw error;
            }
            invalidateOtherEntities();
        },
        [id, type, api, queryClient, patchQuestion, invalidateOtherEntities],
    );

    const skipAllQuestions = useCallback(async () => {
        if (!context) return;
        const snapshot = queryClient.getQueryData<RawIssuesContext>(QUERY_KEY(id, type));
        const unanswered = context.clarifyingQuestions.filter((q) => q.status === 'Unanswered');
        queryClient.setQueryData<RawIssuesContext>(QUERY_KEY(id, type), (old) => {
            if (!old) return old;
            const ids = new Set(unanswered.map((q) => q.questionId));
            return {
                ...old,
                clarifyingQuestions: old.clarifyingQuestions.map((q) =>
                    ids.has(q.questionId) ? { ...q, status: 'Skipped' as QuestionStatus } : q,
                ),
            };
        });
        const results = await Promise.allSettled(unanswered.map((q) => api.skipQuestion(id, q.questionId)));
        invalidateOtherEntities();
        if (results.some((r) => r.status === 'rejected')) {
            queryClient.setQueryData(QUERY_KEY(id, type), snapshot);
            throw new Error('Failed to skip one or more questions');
        }
    }, [context, id, type, api, queryClient, invalidateOtherEntities]);

    const undoQuestion = useCallback(
        async (questionId: string) => {
            const snapshot = queryClient.getQueryData<RawIssuesContext>(QUERY_KEY(id, type));
            patchQuestion(questionId, { status: 'Unanswered', answer: undefined });
            try {
                await api.undoQuestion(id, questionId);
            } catch (error) {
                queryClient.setQueryData(QUERY_KEY(id, type), snapshot);
                throw error;
            }
            invalidateOtherEntities();
        },
        [id, type, api, queryClient, patchQuestion, invalidateOtherEntities],
    );

    return {
        context,
        loading: isLoading,
        unansweredCount,
        unresolvedCount,
        answerQuestion,
        skipQuestion,
        skipAllQuestions,
        undoQuestion,
    };
}
